// Canonical platform language object stored in the branding profile
// (Firestore) and mirrored into localStorage as `platformLanguage`.
export interface PlatformLanguage {
  language: string; // full word used by ngx-translate, e.g. "deutsch"
  isCode: string;   // 2-letter ISO code, e.g. "de"
  lang: string;     // display label, e.g. "Deutsch"
}

export const PLATFORM_LANGUAGES: { [code: string]: PlatformLanguage } = {
  de: { language: 'deutsch', isCode: 'de', lang: 'Deutsch' },
  en: { language: 'englisch', isCode: 'en', lang: 'English' },
  fr: { language: 'französisch', isCode: 'fr', lang: 'Français' },
  it: { language: 'italienisch', isCode: 'it', lang: 'Italiano' },
  nl: { language: 'holländisch', isCode: 'nl', lang: 'Nederlands' }
};

export function getSupportedLanguages(): PlatformLanguage[] {
  return Object.values(PLATFORM_LANGUAGES);
}

// Resolve any language hint (ISO code, ngx-translate word, or locale string)
// into the canonical platform language object. Falls back to German.
export function resolvePlatformLanguage(input?: string | null): PlatformLanguage {
  const value = (input || '').toLowerCase().trim();
  if (!value) return PLATFORM_LANGUAGES['de'];
  if (PLATFORM_LANGUAGES[value]) return PLATFORM_LANGUAGES[value];
  const byWord = Object.values(PLATFORM_LANGUAGES).find(l => l.language === value);
  if (byWord) return byWord;
  if (value.startsWith('de')) return PLATFORM_LANGUAGES['de'];
  if (value.startsWith('en')) return PLATFORM_LANGUAGES['en'];
  if (value.startsWith('fr')) return PLATFORM_LANGUAGES['fr'];
  if (value.startsWith('it')) return PLATFORM_LANGUAGES['it'];
  if (value.startsWith('nl')) return PLATFORM_LANGUAGES['nl'];
  return PLATFORM_LANGUAGES['de'];
}

// Detect the visitor's preferred language from the browser locale.
export function detectBrowserPlatformLanguage(): PlatformLanguage {
  const nav = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : '';
  return resolvePlatformLanguage(nav);
}

// Ensure a platform language is persisted in localStorage so it carries through
// the signup -> pricing -> onboarding flow into the branding profile. Respects an
// already stored choice (returning users, explicit selection) and only seeds a
// system-detected language when nothing has been stored yet. An optional hint
// (e.g. ?lang= param) takes precedence over the browser locale.
export function ensurePlatformLanguagePersisted(hint?: string | null): PlatformLanguage {
  try {
    const existing = (localStorage.getItem('platformLanguage') || '').trim();
    if (existing) return resolvePlatformLanguage(existing);
    const resolved = hint ? resolvePlatformLanguage(hint) : detectBrowserPlatformLanguage();
    localStorage.setItem('platformLanguage', resolved.language);
    localStorage.setItem('onboardingLanguage', resolved.isCode);
    return resolved;
  } catch {
    return resolvePlatformLanguage(hint);
  }
}
